import {Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterOutlet } from '@angular/router';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/service/user.service';
import { IUser } from 'app/core/user/model/user.model';
import { UserComponent } from 'app/layout/common/user/user.component';
import {Subject, takeUntil} from 'rxjs';
import {ClientRelationComponent} from "../../../common/client-relation/client-relation.component";
import {ClientRelationService} from "../../../common/client-relation/service/client-relation.service";
import {ClientRelationResponse} from "../../../common/client-relation/model/client-relation-response";
import {AuthService} from "../../../../core/auth/auth.service";
import {StorageService} from "../../../../core/storage/storage.service";
import {NgIf} from "@angular/common";
import {FuseConfirmationService} from "../../../../../@fuse/services/confirmation";

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        UserComponent,
        MatIconModule,
        MatButtonModule,
        RouterOutlet,
        ClientRelationComponent,
        NgIf,
    ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    user: IUser;
    isAdmin: boolean;
    hasRelations: boolean;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _clientRelationService: ClientRelationService,
        private _authService: AuthService,
        private _storageService: StorageService,
        private _fuseConfirmationService: FuseConfirmationService,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: IUser) => {
                this.user = user;
                this.isAdmin = this._userService.adminCheck(user);
            });

        // Subscribe to the user relations
        this._clientRelationService.getRelations()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((relations: ClientRelationResponse) => {
                this.hasRelations = relations.relatedClients.length > 0;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    exitClientMode(): void {
        this._fuseConfirmationService.open({
            title: "Zakończ tryb widoku klienta",
            message: "Czy napewno chcesz wyjść z trybu widoku klienta?",
            actions: {
                confirm: {
                    show: true,
                    label: 'Tak',
                    color: 'warn',
                },
                cancel: {
                    show: true,
                    label: 'Nie',
                },
            },
        }).afterClosed().subscribe(result => {
            if (result) {
                this._authService.exitClientMode();
            }
        });
    }

    displayClientModeWarning(): boolean {
        return this._storageService.isClientMode();
    }
}
